import {Table, TableCell} from '@paperlessio/sdk/api/models';

export class CellSelection {
  x1: number;
  x2: number;
  y1: number;
  y2: number;

  constructor(v: Partial<CellSelection>) {
    this.x1 = v.x1 ?? 0;
    this.x2 = v.x2 ?? 0;
    this.y1 = v.y1 ?? 0;
    this.y2 = v.y2 ?? 0;
  }

  get width(): number {
    return 1 + this.x2 - this.x1;
  }

  get height(): number {
    return 1 + this.y2 - this.y1;
  }

  get isSingleCellSelection(): boolean {
    return this.width === 1 && this.height === 1;
  }

  clamp(xMin: number, yMin: number, xMax: number, yMax: number): CellSelection {
    const clmp = (val: number, min: number, max: number) => val < min ? min : val > max ? max : val;
    return new CellSelection({
      x1: clmp(this.x1, xMin, xMax),
      y1: clmp(this.y1, yMin, yMax),
      x2: clmp(this.x2, xMin, xMax),
      y2: clmp(this.y2, yMin, yMax)
    });
  }

  collapse(): CellSelection {
    return new CellSelection({
      x1: this.x1,
      y1: this.y1,
      x2: this.x1,
      y2: this.y1
    });
  }

  topDiff(other: CellSelection): number {
    return Math.abs(this.y1 - other.y1);
  }

  rightDiff(other: CellSelection): number {
    return Math.abs(this.x2 - other.x2);
  }

  bottomDiff(other: CellSelection): number {
    return Math.abs(this.y2 - other.y2);
  }

  leftDiff(other: CellSelection): number {
    return Math.abs(this.x1 - other.x1);
  }

  isCellSelected(x: number, y: number) {
    return x >= this.x1
      && x <= this.x2
      && y >= this.y1
      && y <= this.y2;
  }

  isValid(): boolean {
    return this.x1 <= this.x2 && this.y1 <= this.y2;
  }

  moveSelection(direction: 'up' | 'right' | 'down' | 'left') {
    if (!this.isValid()) {
      return;
    }

    const newCellSelection = this.collapse();
    switch (direction) {
      case 'up': {
        newCellSelection.y1 -= 1;
        newCellSelection.y2 -= 1;
        break;
      }
      case 'down': {
        newCellSelection.y1 += 1;
        newCellSelection.y2 += 1;
        break;
      }
      case 'right': {
        newCellSelection.x1 += 1;
        newCellSelection.x2 += 1;
        break;
      }
      case 'left': {
        newCellSelection.x1 -= 1;
        newCellSelection.x2 -= 1;
        break;
      }
    }

    return newCellSelection;
  }

  expandSelection(referenceSelection: CellSelection, direction: 'up' | 'right' | 'down' | 'left') {
    if (!this.isValid()) {
      return;
    }

    const newCellSelection = new CellSelection(this);

    switch (direction) {
      case 'up': {
        if (this.bottomDiff(referenceSelection) > 0) {
          newCellSelection.y2 -= 1;
        } else {
          newCellSelection.y1 -= 1;
        }

        break;
      }
      case 'down': {
        if (this.topDiff(referenceSelection) > 0) {
          newCellSelection.y1 += 1;
        } else {
          newCellSelection.y2 += 1;
        }

        break;
      }
      case 'right': {
        if (this.leftDiff(referenceSelection) > 0) {
          newCellSelection.x1 += 1;
        } else {
          newCellSelection.x2 += 1;
        }

        break;
      }
      case 'left': {
        if (this.rightDiff(referenceSelection) > 0) {
          newCellSelection.x2 -= 1;
        } else {
          newCellSelection.x1 -= 1;
        }

        break;
      }
    }

    return newCellSelection;
  }

  getSelectedChildren(table: Table): TableCell[][] {
    return table.children
      .filter((v, index) => index >= this.y1 && index <= this.y2)
      .map(row => row.children
        .filter((v, index) => index >= this.x1 && index <= this.x2)) as TableCell[][];
  }

  equals(other: CellSelection): boolean {
    return this?.x1 === other?.x1 && this?.y1 === other?.y1 && this?.x2 === other?.x2 && this?.y2 === other?.y2;
  }
}
